import {
  filter,
  isNil,
  join,
  map,
  pipe,
  split,
  trim,
  keys,
  assoc,
  curry,
  reduce,
  path,
} from 'ramda';
import moment from 'moment';
import { getI18n } from 'react-i18next';
import axios from 'axios';
import { SUPPORTED_VIDEO_DOMAINS } from '../constants/resources';
import {
  LOCAL_USER_ID,
  LOCAL_USER_TYPE,
  LOCAL_ORG_ID,
  LOCAL_ORG_NAME,
  LOCAL_CENTER_ID,
  LOCAL_CENTER_NAME,
  LOCAL_SELECTED_GROUP_ID,
  LOCAL_SELECTED_GROUP_NAME,
} from 'constants/localStorage';

export const getThemesByLanguage = (themes) => {
  const currentLanguage = getI18n().language;
  switch (currentLanguage) {
    case 'pt':
      /* themes id to exclude */
      const themesId = [
        14 /* Thanksgiving day */,
        18 /* St. Patrick's Day */,
        22 /* Independence Day */,
      ];
      const ptThemes = themes.filter(({ id }) => themesId.indexOf(id) === -1);
      return ptThemes;

    default:
      return themes;
  }
};

export const toUpperLowerCase = (s) => {
  if (isNil(s)) {
    return null;
  }

  return pipe(
    split(' '),
    filter((x) => x.length > 0),
    map((w) => w[0].toUpperCase() + w.slice(1)),
    join(' '),
    trim,
  )(s);
};

export const mustHaveMenu = (views, pathname) =>
  views.some((view) => {
    const pattern = new RegExp(pathname);
    return pattern.test(view);
  });

// Receive a number of months an returns the number of years and remaining months
export const YearsInMonths = (months) => {
  const years = Math.floor(months / 12);
  const remain = (months / 12) % 1;
  const monthsLeft = Math.round(remain * 12);
  return {
    years,
    months: monthsLeft,
  };
};

/**
 * Get age based on string date with format YYYY-MM-DD,
 * or object date with the shape { year, month, day }.
 * And it will return a string whith the age translated.
 *
 * @param {object | string} dates
 */
export const getAge = (date, t) => {
  const today = moment();
  let birthday;

  if (typeof date === 'string') {
    birthday = moment(date);
  } else if (typeof date === 'object') {
    birthday = moment([date.year, date.month - 1, date.day]);
  }

  const years = today.diff(birthday, 'year');
  birthday.add(years, 'years');

  const months = today.diff(birthday, 'months');
  birthday.add(months, 'months');

  return `${years > 0 ? t('X_YEAR', { years }) : ''} ${
    months > 0 ? t('X_MONTH', { months }) : ''
  }`;
};

/**
 * Pass a string date with format YYYY-MM-DD,
 * to return object with the shape { year, month, day }.
 *
 * @param {object | string} dates
 */
export const getDateObjectByString = (date) => {
  if (typeof date === 'string') {
    return {
      year: moment(date).year(),
      month: moment(date).month() + 1,
      day: moment(date).date(),
    };
  }

  if (typeof date === 'object') {
    return date;
  }

  return date;
};

/**
 * Pass a object date with shape { year, month, day },
 * to return string with the format YYYY-MM-DD.
 *
 * @param {object | string} dates
 */
export const getDateStringByObject = (date) => {
  if (typeof date === 'object') {
    return `${date.year}-${date.month}-${date.day}`;
  }

  if (typeof date === 'string') {
    return date;
  }

  return date;
};


/**
 * Creates a new object with the own properties of the provided object, but the
 * keys renamed according to the keysMap object as `{oldKey: newKey}`.
 * When some key is not found in the keysMap, then it's passed as-is.
 *
 * Keep in mind that in the case of keys conflict is behaviour undefined and
 * the result may vary between various JS engines!
 *
 * @sig {a: b} -> {a: *} -> {b: *}
 */
export const renameKeys = curry((keysMap, obj) =>
  reduce(
    (acc, key) => assoc(keysMap[key] || key, obj[key], acc),
    {},
    keys(obj),
  ));

/**
 * Receives an array and return a formated version with just label and value properties.
 * These label/error values are choosen by passing a path to search in each iteration of
 * the original array.
 *
 * @param {array} list The array to iterate
 * @param {array | number | string} [labelPath] The ramda path to find the label or the direct label
 * @param {array | number | string} [valuePath] The ramda path to find the value or the direct value
 * @param {any} [extraFn] A function that can spread data into the current iteration object.
 */
export const formatOptions = (
  list = [],
  labelPath,
  valuePath,
  extraFn = null,
) => {
  if (list?.length) {
    return list.map((item) => ({
      label: labelPath ? path(labelPath, item) : item,
      value: valuePath ? path(valuePath, item) : item,
      ...(extraFn && extraFn(item)),
    }));
  }
  return [];
};

/**
 * Will only call the function if a certain amount of time has passed.
 * @param {function} callback
 * @param {number} wait
 */
export const debounce = (callback, wait) => {
  let timeout = null;
  return (...args) => {
    const next = () => callback(...args);
    clearTimeout(timeout);
    timeout = setTimeout(next, wait);
  };
};

// deprecated
export const showTerminalLogs = (config = {}, show = true) => {
  if (show) {
    /* eslint-disable */
    if (config.FORCED_MODE) {
      console.log(
        '%cFORCED MODE',
        'background: #b10000; color: #ff8576; font-size: 12px',
      );
    }
    if (config.ENV === config.Environments.STAGE) {
      console.log(
        '%cSTAGING',
        'background: #b15b00; color: #ffa476; font-size: 12px',
      );
      console.log(
        '%cVERSION:',
        'background: #1FADDF; color: #FFF; font-size: 12px',
        config.APP_VERSION,
      );
    }
  }
};

/**
 * This function is used to make the object to send to some backend
 * endpoints.
 *
 * @param {string} resource The name of the primary resource.
 * @param {object | array} values The object with the values to send.
 * @param {string} [secondaryResource] The name of the nested resource.
 */
export const requestObject = (resource, values, secondaryResource) => ({
  [resource]: secondaryResource
    ? {
      [`${secondaryResource}s_attributes`]: values,
    }
    : values,
});

/**
 * Makes the destructuring of the 422 error to check if the classroom is new
 * and has no babies.
 *
 * @param {object} error - The axios error object
 */
export const checkIfGroupEmpty = (error, students = []) => {
  const errors = path(['response', 'data', 'errors'], error);
  return (
    (errors?.babies && errors.babies[0] === "Group doesn't have babies") ||
    students.length === 0
  );
};

export const getWeekStartEndDates = (anyWeekDay) => {
  const weekdayOffset = anyWeekDay.getDay();

  const start = new Date(Number(anyWeekDay));
  start.setDate(start.getDate() - weekdayOffset);
  const end = new Date(Number(start));
  end.setDate(end.getDate() + 6);

  return [start, end]
};

/**
 * Format the given interval string to an adverb ending in -ly. e.g. 'year' -> 'Yearly'.
 *
 * @param {string} interval The given interval. e.g. 'year', 'month' etc.
 * @return {string}
 */
export const formatSubscriptionInterval = (interval) => {
  if (typeof interval !== 'string' || interval.slice(-2) === 'ly') {
    return interval; // Do nothing if the argument is not a string, or it already ends in -ly.
  }

  const titleCasedInterval = interval[0].toUpperCase() + interval.slice(1);

  return `${titleCasedInterval}ly`;
};

/**
 * Generates the weeks of gestation list
 *
 */
export const getWeeks = () => {
  const list = [];
  for (let i = 22; i <= 45; i += 1) {
    list.push(i);
  }
  return list;
};

/**
 * Wait N miliseconds before continuing the code.
 * @param {number} ms number of miliseconds
 */
export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Simulate fetching data
 * @param {any} dummyData Fake data to be return
 * @param {number} delay miliseconds to wait
 */
export const fetchFakeData = async (dummyData, delay) => {
  await sleep(delay);
  return dummyData;
};

/**
 * Create template when sending a contact support e-mail.
 * @param {object} userId the unique id of the user.
 */
export const sendEmail = ({
  userId = 'NA',
  userRole = 'NA',
  email = 'NA',
  body = '',
  planType = '',
  t,
}) => {
  const userAgent = window.navigator.userAgent;
  const path = window.location.pathname;
  return `
    mailto:${t('common:EMAIL_TEMPLATE.EMAIL')}?Subject=${t(
    `common:EMAIL_TEMPLATE.SUBJECT`,
  )}&body=${t(`common:EMAIL_TEMPLATE.BODY`)}%0D%0A
    %0D%0A----------------------------------------------%0D%0A
    ${t('common:EMAIL_TEMPLATE.USER_ID', { userId })}%0D%0A
    ${t('common:EMAIL_TEMPLATE.USER_ROLE', { userRole })}%0D%0A
    ${t('common:EMAIL_TEMPLATE.MY_EMAIL', { email })}%0D%0A
    ${t('common:EMAIL_TEMPLATE.PLAN_TYPE', { planType })}%0D%0A
    ${t('common:EMAIL_TEMPLATE.DEVICE', { userAgent })}%0D%0A
    ${t('common:EMAIL_TEMPLATE.SOURCE')}
`;
};

export const arrayToObject = (array, key) =>
  Object.assign({}, ...array.map(item => ({[item[key]]: item})))

export const isString = (variable) => {
  if (typeof variable === 'string') return true;
  return false;
};

/**
 * Convert inner html string to plain text.
 * "Editable" component from lego1 gets this type of strings
 * @param {string} innerHtmlString the string with html elements as "<br>" and "nbsp;"
 */
export const innerHtmlToPlainText = (innerHtmlString) =>
  innerHtmlString?.replace(/<br ?\/?>/g, '\n')?.replace(/&nbsp;/g, ' ');

export const parseDomainFromUrl = (url = '') => {
  const match = url.match(/:\/\/(www[0-9]?\.)?(.[^/:]+)/i);
  if (match != null && match.length > 2 && typeof match[2] === 'string' && match[2].length > 0) {
    return match[2];
  }
  return null;
}

/**
 * Merge two arrays of objects into one
 * based on key.
 * @param {array} arr1 first array to merge
 * @param {array} arr2 second array to merge
 * @param {string} key1 name of the key
 * @param {string} key2 name of the second key
 */
export function mergeArrayObjects(arr1 = [], arr2 = [], key1 = 'id', key2){
  const newArr = [];
  arr1.map((item,i) => {
    arr2.map((item2, j) => {
      if (item[key1] == item2[key2 || key1]) {
        newArr.push(Object.assign({}, item, item2));
      }
    });
  });
  return newArr;
}

/**
 * Extracts the youtube video ID from an url of type: view, share, embed and a few others.
 * For more info about the regex check: https://stackoverflow.com/a/9102270
 * @param url
 * @returns {*|null}
 */
export const getYoutubeVideoId = (url) => {
  var regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  var match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
}

export const getYoutubeListId = (url) => {
  var regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|\?list=|\&list=)([^#\&\?]*).*/;
  var match = url.match(regExp);

return (match && match[2]) ? match[2] : null;
}

/**
 * Vimeo API for getting embedded video data:
 * https://developer.vimeo.com/api/oembed/videos#embedding-a-video-with-oembed-step-1
 * @param url
 * @returns {Promise<T>}
 */
export const getVimeoVideoData = (url) => {
  return axios.get(`https://vimeo.com/api/oembed.json?url=${url}`)
    .then(res => res.status === 200 ? res.data : null)
    .catch(error => { console.error(error); return null });
}

export const getYoutubeVideoThumbnail = (url, size= 'large') => {
  const ytId = getYoutubeVideoId(url);
  switch (size) {
    case 'small':
      return `https://img.youtube.com/vi/${ytId}/mqdefault.jpg`;
    case 'medium':
      return `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`;
    case 'large':
      return `https://img.youtube.com/vi/${ytId}/sddefault.jpg`;
    default:
      return `https://img.youtube.com/vi/${ytId}/sddefault.jpg`;
  }
}

export const getEmbeddedYtUrl = (url, autoplay= true, showRelated = false) => {
  const isPlayList = /list=/.test(url);

  let ytId = '';
  if (isPlayList) {
    ytId = `videoseries?list=${getYoutubeListId(url)}&`;
  } else {
    ytId = `${getYoutubeVideoId(url)}?`;
  }

  return `https://www.youtube.com/embed/${ytId}autoplay=${autoplay ? 1 : 0}&rel=${showRelated ? 1 : 0}`;
}

export const getEmbeddedVimeoUrl = async (url, autoplay= true, showRelated = false) => {
  const vimeoData = await getVimeoVideoData(url);
  return `https://player.vimeo.com/video/${vimeoData.video_id}`;
}

export const getEmbeddedVideoData = async (url) => {
  if (url !== '') {
    const domain = parseDomainFromUrl(url);
    if (SUPPORTED_VIDEO_DOMAINS.youtube.some(dom => dom === domain)) {
      return { url: getEmbeddedYtUrl(url), thumbnail: getYoutubeVideoThumbnail(url) };
    }
    if (SUPPORTED_VIDEO_DOMAINS.vimeo.some(dom => dom === domain)) {
      const vimeoData = await getVimeoVideoData(url);
      return { url: `https://player.vimeo.com/video/${vimeoData.video_id}`, thumbnail: vimeoData.thumbnail_url };
    }
  }

  const embedDataInitial = { url: '', thumbnail: '' };

  return embedDataInitial;
};

 /* Delete multiple properties of an object
 * @param {object} obj The object to search properties to delete
 * @param {array} props The array of properties to delete
 */
export function deleteProps (obj, props) {
  for (const p of props) {
    delete obj[p];
  }
}

export const formatDate = (date, formatStr, locale = 'es') => {
  if (moment(date).isValid()) {
    const defaultLang = localStorage.getItem('locale') ? localStorage.getItem('locale') : 'es';
    const lang = locale || defaultLang;
    moment.locale(lang || 'en');
    return moment(date).format(formatStr)
    // return moment(date.replace('Z', '').replace('z', '')).format(formatStr);
  }

  return 'Invalid Date';
}

export const formatDateUtc = (date, formatStr) => {
  if (moment(date).isValid()) {
    // const defaultLang = localStorage.getItem('locale') ? localStorage.getItem('locale') : 'en';
    // moment.locale(defaultLang || 'en');
    if(formatStr) {
      return moment(date).utc().local().format(formatStr);
    } else {
      return moment(date).utc().local();
    }
  }

  return 'Invalid Date';
}


export const sendReportEmail = (t, subject = '', body = '') => {
  const userAgent = window.navigator.userAgent;
  const path = `${window.location.pathname}${window.location.search}`;
  const userId = LOCAL_USER_ID();
  const userRole = LOCAL_USER_TYPE();
  const orgId = LOCAL_ORG_ID();
  const activeCenterId = LOCAL_CENTER_ID();
  const activeGroupId = LOCAL_SELECTED_GROUP_ID();
  const org = LOCAL_ORG_NAME();
  const activeCenter = LOCAL_CENTER_NAME();
  const activeGroup = LOCAL_SELECTED_GROUP_NAME();

  return `
    mailto:${t('common:EMAIL_MSG.EMAIL')}?Subject=${t(`common:${subject}`)}
    &body=${t(`common:${body}`)}%0D%0A
    %0D%0A%0D%0A%0D%0A%0D%0A
    %0D%0A----------------------------------------------%0D%0A
    ${t('common:EMAIL_MSG.USER_ID', { userId })}%0D%0A
    ${t('common:EMAIL_MSG.USER_AGENT', { userAgent })}%0D%0A
    ${t('common:EMAIL_MSG.USER_ROLE', { userRole })}%0D%0A
    ${t('common:EMAIL_MSG.USER_ORG', { org: `${org} - ${orgId}` })}%0D%0A
    ${t('common:EMAIL_MSG.USER_CENT', { activeCenter: `${activeCenter} - ${activeCenterId}` })}%0D%0A
    ${t('common:EMAIL_MSG.USER_GRP', { activeGroup: `${activeGroup} - ${activeGroupId}` })}%0D%0A
    ${t('common:EMAIL_MSG.PATH', { path })}
  `;
};

export const unaccent = s => {
  let str = s;
  const map = {
    'a' : 'á|à|ã|â|À|Á|Ã|Â',
    'e' : 'é|è|ê|É|È|Ê',
    'i' : 'í|ì|î|Í|Ì|Î',
    'o' : 'ó|ò|ô|õ|Ó|Ò|Ô|Õ',
    'u' : 'ú|ù|û|ü|Ú|Ù|Û|Ü',
    'c' : 'ç|Ç',
    'n' : 'ñ|Ñ'
  };

  for (var pattern in map) {
    str = str.replace(new RegExp(map[pattern], 'g'), pattern);
  }

  return str;
};

export const genderAdposition = gender => (gender ? (gender === 'male' ? 'do' : 'da') : 'de');

export const genderPrepositional = gender => (gender === 'male' ? 'ao' : 'à');
